import PageSkeleton from "@/components/page-skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast, useToastError } from "@/components/ui/use-toast";
import { useListings } from "@/contexts/listings-provider";
import { useUser } from "@/contexts/user-provider";
import { getTeamUserRoleLabel } from "@/enums/team-user-role";
import { useApi } from "@/hooks/useApi";
import { useArePeopleVisible } from "@/hooks/useArePeopleVisible";
import { useTranslation } from "@/hooks/useTranslation";
import { getAvatarColor } from "@/lib/avatar-color";
import { convertDateToDistanceNow } from "@/lib/date";
import { getInitialsFromParts } from "@/lib/initials";
import { cn } from "@/lib/utils";
import { TeamMemberStatsDTO, UserListingDTO } from "@shaarp/common";
import {
  Building2Icon,
  ClockIcon,
  EyeIcon,
  ListIcon,
  UserPlusIcon,
  UsersIcon,
  XIcon,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { NavLink, useParams } from "react-router-dom";

export default function TeamMemberDetailPage() {
  const api = useApi();
  const { t, currentLanguage } = useTranslation();
  const toastError = useToastError();
  const { userId } = useParams();
  const { userListings, apiFetchListings, apiFetchCollaboratorsListings } =
    useListings();
  const { teamUser } = useUser();
  // A team whose contact switch is off sees no key contacts (#757): neither the
  // teammate's contact count nor their contact lists.
  const arePeopleVisible = useArePeopleVisible();

  const [stats, setStats] = useState<TeamMemberStatsDTO | null>(null);
  const [allMemberListings, setMemberListings] = useState<UserListingDTO[]>(
    [],
  );
  const memberListings = arePeopleVisible
    ? allMemberListings
    : allMemberListings.filter((l) => l.listingType !== "employees");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isAssignOpen, setIsAssignOpen] = useState<boolean>(false);
  const [assigningListingId, setAssigningListingId] = useState<string | null>(
    null,
  );

  const fetchData = useCallback(
    async (signal?: AbortSignal) => {
      if (!userId) {
        return;
      }
      setIsLoading(true);
      try {
        const [statsRes, listingsRes] = await Promise.all([
          api.get<TeamMemberStatsDTO>(
            `/listings/team-members/${userId}/stats`,
            { signal },
          ),
          api.get<UserListingDTO[]>(
            `/listings/team-members/${userId}/listings`,
            { signal },
          ),
        ]);
        if (signal?.aborted) {
          return;
        }
        setStats(statsRes.data);
        setMemberListings(listingsRes.data);
      } catch {
        if (signal?.aborted) {
          return;
        }
        toastError();
      } finally {
        if (!signal?.aborted) {
          setIsLoading(false);
        }
      }
    },
    [api, toastError, userId],
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, [fetchData]);

  const formatRelative = (
    date: Date | string | null | undefined,
  ): string => {
    if (!date) {
      return "—";
    }
    return convertDateToDistanceNow(new Date(date), {
      languageOverride: currentLanguage,
    });
  };

  const onRemoveAssignation = async (listing: UserListingDTO) => {
    try {
      await api.delete(`/listing/${listing.id}/assignees/${userId}`);
      setMemberListings((prev) =>
        prev.map((l) =>
          l.id === listing.id ? { ...l, assignation: undefined } : l,
        ),
      );
      toast({
        variant: "default",
        title: t("listing.page.team.actions.removeSuccess"),
      });
      fetchData();
      apiFetchCollaboratorsListings({ force: true });
    } catch {
      toastError();
    }
  };

  const onAssignListing = async (listingId: string) => {
    if (!userId) {
      return;
    }
    setAssigningListingId(listingId);
    try {
      await api.post(`/listing/${listingId}/assignees`, {
        userIds: [userId],
      });
      toast({
        variant: "default",
        title: t("listing.page.team.actions.assignSuccess"),
      });
      setIsAssignOpen(false);
      fetchData();
      apiFetchListings({ force: true });
      apiFetchCollaboratorsListings({ force: true });
    } catch {
      toastError();
    } finally {
      setAssigningListingId(null);
    }
  };

  if (isLoading || !stats) {
    return (
      <div className="absolute top-0 left-0 p-8 pt-6 compact:p-6 compact:pt-4 w-full h-full">
        <PageSkeleton />
      </div>
    );
  }

  const colorClass = getAvatarColor(stats.user.id);
  const initials = getInitialsFromParts(
    stats.user.firstName,
    stats.user.lastName,
  );

  const startOfMonth = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    1,
  ).getTime();
  const thisMonthCount = memberListings.filter(
    (l) =>
      l.assignation?.assignedAt &&
      new Date(l.assignation.assignedAt).getTime() >= startOfMonth,
  ).length;

  const memberListingIds = new Set(allMemberListings.map((l) => l.id));
  const availableListings = userListings.filter(
    (l) => !memberListingIds.has(l.id),
  );

  return (
    <div className="overflow-auto absolute top-0 left-0 p-8 pt-6 compact:p-6 compact:pt-4 space-y-5 w-full h-full">
      <div className="flex justify-between items-start gap-4 p-5 rounded-xl border bg-background">
        <div className="flex gap-3 items-center">
          <Avatar className="w-14 h-14 ring-2 ring-majorelle/20 ring-offset-2 ring-offset-background">
            <AvatarImage src={stats.user.imageUrl} />
            <AvatarFallback
              className={cn("text-lg font-semibold", colorClass)}
            >
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="space-y-1">
            <h2 className="text-2xl font-bold tracking-tight">
              {stats.user.fullName}
            </h2>
            <div className="flex gap-2 items-center">
              <Badge className="bg-majorelle/10 text-majorelle border-transparent hover:bg-majorelle/10">
                {getTeamUserRoleLabel(stats.user.role, t)}
              </Badge>
              {stats.lastActivityAt && (
                <>
                  <span className="text-xs text-muted-foreground">·</span>
                  <span className="text-xs text-muted-foreground">
                    {formatRelative(stats.lastActivityAt)}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        <Popover open={isAssignOpen} onOpenChange={setIsAssignOpen}>
          <PopoverTrigger asChild>
            <Button>
              <UserPlusIcon className="w-4 h-4" />
              {t("listing.page.team.assignButton")}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="p-0 w-80" align="end">
            <Command>
              <CommandInput
                placeholder={t("listing.page.team.assignPopover.search")}
              />
              <CommandList>
                <CommandEmpty>
                  {t("listing.page.team.assignPopover.empty")}
                </CommandEmpty>
                <CommandGroup
                  heading={t("listing.page.team.assignPopover.title")}
                >
                  {availableListings.map((listing) => (
                    <CommandItem
                      key={listing.id}
                      value={listing.title}
                      disabled={assigningListingId === listing.id}
                      onSelect={() => onAssignListing(listing.id)}
                      className="flex gap-2 items-center cursor-pointer"
                    >
                      <span className="flex justify-center items-center w-7 h-7 rounded-md shrink-0 bg-muted/70">
                        {listing.listingType === "employees" ? (
                          <UsersIcon className="w-4 h-4 text-muted-foreground" />
                        ) : (
                          <Building2Icon className="w-4 h-4 text-muted-foreground" />
                        )}
                      </span>
                      <span className="flex-1 truncate">{listing.title}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      <div
        className={cn(
          "grid gap-4",
          arePeopleVisible ? "grid-cols-4" : "grid-cols-3",
        )}
      >
        <div className="p-4 space-y-2 rounded-xl border bg-background">
          <div className="flex gap-2 items-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <ListIcon className="w-4 h-4" />
            {t("listing.page.team.stats.listings")}
          </div>
          <div className="text-2xl font-bold">
            {stats.assignedListingsCount}
          </div>
          <div className="text-xs text-muted-foreground">
            {t("listing.page.team.stats.thisMonth", {
              count: thisMonthCount,
            })}
          </div>
        </div>
        <div className="p-4 space-y-2 rounded-xl border bg-background">
          <div className="flex gap-2 items-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <Building2Icon className="w-4 h-4" />
            {t("listing.page.team.stats.companies")}
          </div>
          <div className="text-2xl font-bold">
            {stats.assignedCompaniesCount}
          </div>
        </div>
        {arePeopleVisible && (
          <div className="p-4 space-y-2 rounded-xl border bg-background">
            <div className="flex gap-2 items-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <UsersIcon className="w-4 h-4" />
              {t("listing.page.team.stats.contacts")}
            </div>
            <div className="text-2xl font-bold">
              {stats.assignedEmployeesCount}
            </div>
          </div>
        )}
        <div className="p-4 space-y-2 rounded-xl border bg-background">
          <div className="flex gap-2 items-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <ClockIcon className="w-4 h-4" />
            {t("listing.page.team.stats.lastActivity")}
          </div>
          <div className="text-base font-semibold">
            {formatRelative(stats.lastActivityAt)}
          </div>
          <div className="text-xs text-muted-foreground">
            {t("listing.page.team.stats.lastActivitySubtitle")}
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex flex-wrap gap-2 justify-between items-end">
          <h3 className="text-lg font-semibold">
            {t("listing.page.team.listingsTitle", {
              firstName: stats.user.firstName,
            })}
          </h3>
        </div>

        {memberListings.length === 0 ? (
          <p className="py-8 text-sm text-center text-muted-foreground">
            {t("listing.page.team.empty")}
          </p>
        ) : (
          <div className="overflow-hidden rounded-xl border bg-background">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("listing.page.team.headers.list")}</TableHead>
                  <TableHead>
                    {t("listing.page.team.headers.assignation")}
                  </TableHead>
                  <TableHead>
                    {t("listing.page.team.headers.companies")}
                  </TableHead>
                  <TableHead>
                    {t("listing.page.team.headers.assignedAt")}
                  </TableHead>
                  <TableHead>
                    {t("listing.page.team.headers.lastActivity")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("listing.page.team.headers.actions")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {memberListings.map((listing) => {
                  const isAssigned = Boolean(listing.assignation);
                  const assignedBy = listing.assignation?.assignedBy;
                  const isAssignedByCurrentUser =
                    isAssigned &&
                    Boolean(teamUser?.id) &&
                    assignedBy?.id === teamUser?.id;
                  const assignedLabel = isAssignedByCurrentUser
                    ? t("listing.page.team.types.assignedByYou")
                    : assignedBy
                      ? t("listing.page.team.types.assignedBy", {
                          name: assignedBy.firstName || assignedBy.fullName,
                        })
                      : t("listing.page.team.types.assigned");
                  const count =
                    listing.listingType === "employees"
                      ? listing.employeesCount
                      : listing.companiesCount;
                  return (
                    <TableRow key={listing.id}>
                      <TableCell>
                        <span className="flex gap-3 items-center">
                          <span className="flex justify-center items-center w-9 h-9 rounded-lg shrink-0 bg-majorelle/10">
                            {listing.listingType === "employees" ? (
                              <UsersIcon className="w-4 h-4 text-majorelle" />
                            ) : (
                              <Building2Icon className="w-4 h-4 text-majorelle" />
                            )}
                          </span>
                          <span className="font-semibold">{listing.title}</span>
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={cn(
                            "px-2.5 py-0.5 text-xs font-medium rounded-full border-transparent",
                            isAssigned
                              ? "bg-majorelle/10 text-majorelle hover:bg-majorelle/10"
                              : "bg-muted text-muted-foreground hover:bg-muted",
                          )}
                        >
                          {isAssigned
                            ? assignedLabel
                            : t("listing.page.team.types.personal")}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">{count}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {listing.assignation?.assignedAt
                          ? new Date(
                              listing.assignation.assignedAt,
                            ).toLocaleDateString(currentLanguage)
                          : "—"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        <span className="flex gap-1.5 items-center">
                          <ClockIcon className="w-3.5 h-3.5 shrink-0" />
                          <span>{formatRelative(listing.lastUpdatedAt)}</span>
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end items-center">
                          <Button
                            asChild
                            variant="ghost"
                            size="sm"
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950/30"
                          >
                            <NavLink to={`/listing/${listing.id}`}>
                              <EyeIcon className="w-4 h-4" />
                              {t("listing.page.team.actions.view")}
                            </NavLink>
                          </Button>
                          {isAssigned && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onRemoveAssignation(listing)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
                            >
                              <XIcon className="w-4 h-4" />
                              {t("listing.page.team.actions.remove")}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
